import { memo, useCallback } from 'react';

import { type InvitedMember, useTrackInvitedMember } from '@air/analytics';
import { WorkspaceMemberType } from '@air/api/types';
import { matchesAirror, OVER_MAX_MEMBERS } from '@air/errors';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import {
  InviteByEmailInput,
  type InviteByEmailInputProps,
} from '~/components/Modals/InviteMembers/InviteMembersModal/InviteByEmailInput';
import { MembersList } from '~/components/Modals/InviteMembers/InviteMembersModal/MembersList';
import { MemberLimitModal } from '~/components/Modals/MemberLimitModal';
import { useAddWorkspaceMembers } from '~/swr-hooks/members/useAddWorkspaceMembers';
import { sanitizeEmail } from '~/utils/EmailUtils';

export type InviteByEmailProps = {
  workspaceTitle: string;
  location: InvitedMember['location'];
  onClickViewAllUsers: () => void;
  onInviteSuccess?: (emails: string[]) => void;
};

export const InviteByEmail = memo(
  ({ workspaceTitle, location, onClickViewAllUsers, onInviteSuccess }: InviteByEmailProps) => {
    const { addWorkspaceMember } = useAddWorkspaceMembers();
    const { showToast } = useToasts();
    const [showLimitMemberModal] = useAirModal(MemberLimitModal);
    const { trackInvitedMember } = useTrackInvitedMember();

    const handleAddMemberError = useCallback(
      (error: unknown) => {
        if (matchesAirror(error, OVER_MAX_MEMBERS)) {
          showLimitMemberModal();
          return;
        }

        showToast('Failed to add users. Please try again later.');
      },
      [showLimitMemberModal, showToast],
    );

    const handleSubmitMembers = useCallback(
      async ({ emails, selectedRole }: Parameters<InviteByEmailInputProps['submitMembers']>[0]) => {
        const sanitizedEmails = emails.map((email) => sanitizeEmail(email));

        await addWorkspaceMember(
          sanitizedEmails.map((email) => ({
            email,
            roleId: selectedRole.id,
            type: WorkspaceMemberType.member,
            emailTemplate: 'default',
          })),
        );

        sanitizedEmails.forEach(() => {
          trackInvitedMember({ location, role: selectedRole.displayName, type: selectedRole.type });
        });

        onInviteSuccess?.(sanitizedEmails);

        showToast(sanitizedEmails.length === 1 ? 'Invitation sent' : 'Invitations sent');
      },
      [addWorkspaceMember, location, onInviteSuccess, showToast, trackInvitedMember],
    );

    return (
      <div className="mt-6">
        <div className="ads:text-metadata text-14 text-pigeon-400">
          Invite your teammates to join {workspaceTitle}. Enter an email, choose permission levels, and send.
        </div>
        <InviteByEmailInput submitMembers={handleSubmitMembers} onError={handleAddMemberError} />
        <MembersList onClickViewAllUsers={onClickViewAllUsers} />
      </div>
    );
  },
);

InviteByEmail.displayName = 'InviteByEmail';
